import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { PersonItem } from '@models/custom-fields.model';
import {
  CountryItem,
  DeviceLostOption,
  OrderActionType,
  OrderCenterAction,
  OrderCenterResponse
} from '@models/dashboard.model';
import { DeviceAsset, LineAsset } from '@models/dashboard.model';
import { hasDeviceId, hasLineId } from '@models/functions.model';
import { ShoppingCartData } from '@models/shopping-cart.model';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityManagerService } from '@services/activity/manager/activity-manager.service';
import { DashboardService } from '@services/dashboard/dashboard.service';
import { ModalService } from '@services/modal/modal.service';
import { SessionService } from '@services/session/session.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { CarouselComponent } from '@shared-components/carousel/carousel.component';
import { ListboxComponent } from '@shared-components/listbox/listbox.component';
import { SelectOrderForComponent } from '@shared-components/select-order-for/select-order-for.component';
import { Subscription } from 'rxjs';
import { DataLoaderDirective } from 'tangoe-design-system';

const DEVICE_LOST_OPTIONS: DeviceLostOption[] = [
  {
    id: 1,
    name: 'Yes, my device is lost or stolen'
  },
  {
    id: 2,
    name: 'No, I have my device'
  }
];

@Component({
  selector: 'ts-order-center',
  templateUrl: './order-center.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DataLoaderDirective,
    TranslationPipe,
    CarouselComponent,
    ListboxComponent
  ]
})
export class OrderCenterComponent implements OnInit, OnDestroy {
  @ViewChild('countriesList') countries_list_template: TemplateRef<any>;
  @ViewChild('isLostOrStolen') lost_or_stolen_template: TemplateRef<any>;
  order_center_actions: OrderCenterAction[];
  order_center_action: OrderCenterAction;
  order_action_type: OrderActionType;
  countries: CountryItem[] = [];
  person: FormControl = new FormControl<PersonItem | null>(null);
  selected_country: CountryItem | undefined = undefined;
  device_lost_options: DeviceLostOption[] = DEVICE_LOST_OPTIONS;
  lost_stolen: boolean | undefined = undefined;
  private subscription: Subscription = new Subscription();

  constructor(
    private activity_manager_svc: ActivityManagerService,
    private dashboard_svc: DashboardService,
    private modal_svc: ModalService,
    private session_svc: SessionService,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService
  ) {}

  ngOnInit(): void {
    this.device_lost_options[0].name = this.translation_svc.translate('device_lost_stolen_yes');
    this.device_lost_options[1].name = this.translation_svc.translate('device_lost_stolen_no');

    this.subscription.add(
      this.dashboard_svc.orderCenterActions().subscribe({
        next: (res: OrderCenterResponse) => (this.order_center_actions = res.order_groups)
      })
    );

    this.subscription.add(
      this.dashboard_svc.userCountryList().subscribe((res: CountryItem[]) => {
        this.countries = res;
        this.selected_country = res[0];
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  get shoppingCartParams(): ShoppingCartData {
    return {
      orderTypeId: this.activity_manager_svc.state().orderTypeId(),
      shopping_cart_items: [],
      lost_stolen: this.lost_stolen,
      order_for_person: this.person.value
    };
  }

  hasLineId = (line: LineAsset): boolean => hasLineId(line);

  hasDeviceId = (device: DeviceAsset): boolean => hasDeviceId(device);

  onCarouselItemClick(action: OrderCenterAction): void {
    this.selectOrderForModal(action, undefined, true);
  }

  selectOrderForModal(
    action: OrderCenterAction,
    override_step_id?: number,
    reset_person?: boolean
  ): void {
    let active_element: HTMLElement = document.activeElement as HTMLElement;
    active_element.blur();

    if (reset_person) this.person.setValue(null);

    const ofo_enabled: boolean =
      this.session_svc.getAuthorizedToOfo() && action.is_order_for_other_supported_by_group;
    const is_bulk_order: boolean = action.category === 'bulk_order' ? true : false;
    this.order_center_action = action;

    const modalRef: NgbModalRef = this.modal_svc.open(SelectOrderForComponent, {
      backdrop: 'static',
      centered: true,
      onClose: () => (this.selected_country = this.countries[0])
    });
    modalRef.componentInstance.ofo_enabled = ofo_enabled;
    modalRef.componentInstance.my_order_center_actions = [...this.order_center_actions];
    modalRef.componentInstance.order_center_action = { ...this.order_center_action };
    modalRef.componentInstance.step_id.setValue(
      override_step_id ?? (is_bulk_order ? 2 : ofo_enabled ? 1 : 3)
    );
    modalRef.componentInstance.is_bulk_order = is_bulk_order;
    if (this.person.value) modalRef.componentInstance.person.setValue(this.person.value);

    modalRef.componentInstance.order_action_type.subscribe(
      (val: { orderActionType: OrderActionType; person: PersonItem }) => {
        this.person.setValue(val.person);
        this.orderActionType(val.orderActionType);
      }
    );
  }

  onCountryModalBack(): void {
    this.modal_svc.dismissLastModal();

    setTimeout(() => {
      this.selectOrderForModal(this.order_center_action, 3);
    }, 0);
  }

  selectCountry(country: CountryItem): void {
    this.selected_country = country;
  }

  countryChoiceValidated(): void {
    if (!this.order_action_type) return;

    switch (this.order_action_type.activity_type_name) {
      case 'BulkPendingLine':
      case 'LinePort':
      case 'NewDeviceService':
      case 'PendingLine':
        this.navigateToActivityRoute();
        break;

      default:
        break;
    }
  }

  countryModalDismiss(): void {
    this.modal_svc.dismissLastModal('User dismissed modal', () => {
      this.selected_country = this.countries[0];
    });
  }

  isDeviceStolen(val: DeviceLostOption): void {
    this.lost_stolen = val.id === 1;
    this.navigateToActivityRoute();
  }

  private orderActionType(orderActionType: OrderActionType): void {
    this.order_action_type = orderActionType;
    this.orderTypeAction(this.order_action_type);
  }

  private navigateToActivityRoute(): void {
    this.activity_manager_svc.state().setActivity({
      ...this.order_action_type,
      name: this.order_center_action.name
    });
    if (this.selected_country) this.activity_manager_svc.state().setCountry(this.selected_country);
    if (this.lost_stolen !== undefined)
      this.activity_manager_svc.state().setParameter('lost_stolen', this.lost_stolen);

    if (this.order_center_action.is_order_for_other_supported_by_group && this.person.value) {
      this.shopping_svc.sendShoppingItems(this.shoppingCartParams, true).subscribe({
        next: (res: ShoppingCartData) => {
          this.activity_manager_svc.state().setOrderForPerson(res.order_for_person);
          this.activity_manager_svc.state().saveConfig();
          this.modal_svc.dismissAll();
          this.activity_manager_svc.navigateToNextStep();
        },
        error: (err: HttpErrorResponse) => {}
      });
      return;
    }

    this.activity_manager_svc.state().saveConfig();
    this.modal_svc.dismissAll();
    this.activity_manager_svc.navigateToNextStep();
  }

  private orderTypeAction(orderActionType: OrderActionType): void {
    this.activity_manager_svc.state().setAdminOverrideSupported(orderActionType.is_admin_supported);

    switch (orderActionType.activity_type_name) {
      // Please keep switch cases alphabetized.
      case 'LineDeviceUpgrade':
      case 'LineSuspend':
        this.modal_svc.transitionToModal(this.lost_or_stolen_template, {
          backdrop: 'static',
          centered: true
        });
        break;
      case 'LineUnsuspend':
        this.lost_stolen = false;
        this.navigateToActivityRoute();
        break;

      case 'BulkPendingLine':
      case 'LinePort':
      case 'NewDeviceService':
      case 'PendingLine':
        if (this.countries.length > 1)
          this.modal_svc.transitionToModal(this.countries_list_template, {
            backdrop: 'static',
            centered: true
          });
        else this.navigateToActivityRoute();
        break;

      default:
        this.navigateToActivityRoute();
        break;
    }
  }
}
